import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axios";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";

const IntegrationTab = () => {
  // =========================
  // SMTP INTEGRATION STATE
  // =========================
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testingSmtpId, setTestingSmtpId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [form, setForm] = useState({
    id: null,
    name: "",
    host: "",
    port: 587,
    username: "",
    password: "",
    encryption: "tls",
    from_email: "",
    from_name: "",
  });

  // =========================
  // OTHER INTEGRATIONS STATE
  // =========================
const [otherIntegrations, setOtherIntegrations] = useState([]);

const fetchOtherIntegrations = async () => {
  try {
    const response = await axiosInstance.get("/integrations");
    setOtherIntegrations(response.data);
  } catch (error) {
    console.error(error);
  }
};
  const [showOtherModal, setShowOtherModal] = useState(false);
  const [editingOtherIntegration, setEditingOtherIntegration] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [newOtherIntegration, setNewOtherIntegration] = useState({
    name: "",
    type: "",
    endpoint: "",
    apiKey: "",
    syncFrequency: "daily",
    isEnabled: true
  });

  const integrationTypes = [
    { value: "ERP System", label: "ERP System" },
    { value: "Procurement Platform", label: "Procurement Platform" },
    { value: "Mobile Application", label: "Mobile Application" },
    { value: "Authentication", label: "Authentication Service" },
    { value: "Service Management", label: "Service Management" },
    { value: "Database", label: "External Database" },
    { value: "API Gateway", label: "API Gateway" }
  ];

  const syncFrequencyOptions = [
    { value: "realtime", label: "Real-time" },
    { value: "hourly", label: "Every Hour" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "manual", label: "Manual Only" }
  ];

  // =========================
  // SMTP FETCH
  // =========================
  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/smtp-integrations");
      setIntegrations(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();        // SMTP
    fetchOtherIntegrations();   // Other integrations
  }, []);


  // =========================
  // SMTP CREATE (FIRST TIME)
  // =========================
  const handleCreate = async () => {
    if (!form.name || !form.host || !form.username || !form.password || !form.from_email) {
      alert("Please fill in all required fields (Name, Host, Username, Password, From Email)");
      return;
    }
    
    try {
      await axiosInstance.post("/smtp-integrations", {
        name: form.name,
        host: form.host,
        port: form.port,
        username: form.username,
        password: form.password,
        encryption: form.encryption,
        from_email: form.from_email,
        from_name: form.from_name,
      });
      await fetchIntegrations();
      closeModal();
      alert("SMTP configuration created successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to create SMTP configuration");
    }
  };

  // =========================
  // SMTP UPDATE
  // =========================
  const handleUpdate = async () => {
    if (!editing || !form.id) {
      alert("No SMTP configuration to update.");
      return;
    }
    
    const updateData = {
      name: form.name,
      host: form.host,
      port: form.port,
      username: form.username,
      encryption: form.encryption,
      from_email: form.from_email,
      from_name: form.from_name,
      is_enabled: true
    };
    
    // Only include password if it was provided (user wants to change it)
    if (form.password && form.password.trim() !== "") {
      updateData.password = form.password;
    }
    
    try {
      await axiosInstance.put(`/smtp-integrations/${form.id}`, updateData);
      await fetchIntegrations();
      closeModal();
      alert("SMTP configuration updated successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to update SMTP configuration");
    }
  };

  // =========================
  // SMTP DELETE
  // =========================
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this SMTP configuration?")) return;
    try {
      await axiosInstance.delete(`/smtp-integrations/${id}`);
      await fetchIntegrations();
      alert("SMTP configuration deleted successfully!");
    } catch (error) {
      console.error(error);
      alert("Failed to delete SMTP configuration");
    }
  };

  // =========================
  // SMTP TEST
  // =========================
  const handleTest = async (id) => {
    setTestingSmtpId(id);
    try {
      const response = await axiosInstance.post(`/smtp-integrations/${id}/test`);
      alert(response.data?.message || "SMTP Connected Successfully!");
      await fetchIntegrations();
    } catch (error) {
      alert(error.response?.data?.message || "SMTP Connection Failed");
      console.error(error);
      await fetchIntegrations();
    } finally {
      setTestingSmtpId(null);
    }
  };

  // =========================
  // SMTP EDIT
  // =========================
  const handleEdit = (integration) => {
    if (!integration || !integration.id) {
      alert("No SMTP configuration found to edit.");
      return;
    }
    setIsCreatingNew(false);
    setEditing(integration);
    setForm({
      id: integration.id,
      name: integration.name || "",
      host: integration.host || "",
      port: integration.port || 587,
      username: integration.username || "",
      password: "", // Don't show password for security
      encryption: integration.encryption || "tls",
      from_email: integration.from_email || "",
      from_name: integration.from_name || "",
    });
    setShowModal(true);
  };

  // =========================
  // SMTP CREATE NEW (FIRST TIME)
  // =========================
  const handleCreateNew = () => {
    setIsCreatingNew(true);
    setEditing(null);
    setForm({
      id: null,
      name: "",
      host: "",
      port: 587,
      username: "",
      password: "",
      encryption: "tls",
      from_email: "",
      from_name: "",
    });
    setShowModal(true);
  };

  // =========================
  // SMTP CLOSE
  // =========================
  const closeModal = () => {
    setEditing(null);
    setIsCreatingNew(false);
    setShowModal(false);
    setForm({
      id: null,
      name: "",
      host: "",
      port: 587,
      username: "",
      password: "",
      encryption: "tls",
      from_email: "",
      from_name: "",
    });
  };

  // =========================
  // OTHER INTEGRATIONS FUNCTIONS
  // =========================
  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'disconnected':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const formatLastSync = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else {
      return date?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  };
const handleTestOtherConnection = async (integration) => {

    setTestingConnection(integration.id);

    try {

        const response = await axiosInstance.post(

            `/integrations/${integration.id}/test`

        );

        alert(response.data.message);

        fetchOtherIntegrations();

    } catch (error) {

        alert(error.response?.data?.message || "Connection Failed");

    } finally {

        setTestingConnection(null);

    }

};

const handleToggleOtherIntegration = async (id) => {

    try {

        await axiosInstance.patch(`/integrations/${id}/toggle`);

        fetchOtherIntegrations();

    } catch (error) {

        console.error(error);

    }

};
 const handleAddOtherIntegration = async () => {

    const payload = {
        name: newOtherIntegration.name,
        type: newOtherIntegration.type,
        endpoint: newOtherIntegration.endpoint,
        api_key: newOtherIntegration.apiKey,
        sync_frequency: newOtherIntegration.syncFrequency,
        is_enabled: newOtherIntegration.isEnabled,
    };

    console.log(payload);

    try {

        const response = await axiosInstance.post("/integrations", payload);

        console.log(response.data);

        fetchOtherIntegrations();

        closeOtherModal();

        alert("Integration Added Successfully");

    } catch (error) {

        console.log(error.response);

        console.log(error.response?.data);

        alert(JSON.stringify(error.response?.data));

    }

};

  const handleEditOtherIntegration = (integration) => {
    setEditingOtherIntegration(integration);
    setNewOtherIntegration({ ...integration });
    setShowOtherModal(true);
  };


const handleUpdateOtherIntegration = async () => {

    const payload = {
        name: newOtherIntegration.name,
        type: newOtherIntegration.type,
        endpoint: newOtherIntegration.endpoint,
        api_key: newOtherIntegration.apiKey,
        sync_frequency: newOtherIntegration.syncFrequency,
        is_enabled: newOtherIntegration.isEnabled,
    };

    console.log(payload);

    try {

        const response = await axiosInstance.put(
            `/integrations/${editingOtherIntegration.id}`,
            payload
        );

        console.log(response.data);

        await fetchOtherIntegrations();

        closeOtherModal();

        alert("Integration Updated Successfully");

    } catch (error) {

        console.log(error.response);

        console.log(error.response?.data);

        alert(error.response?.data?.message || "Update Failed");
    }
};



const handleDeleteOtherIntegration = async (id) => {

    if (!window.confirm("Delete Integration?")) return;

    try {

        await axiosInstance.delete(`/integrations/${id}`);

        fetchOtherIntegrations();

    } catch (error) {

        console.error(error);

    }

};

  const closeOtherModal = () => {
    setShowOtherModal(false);
    setEditingOtherIntegration(null);
    setNewOtherIntegration({
      name: "",
      type: "",
      endpoint: "",
      apiKey: "",
      syncFrequency: "daily",
      isEnabled: true
    });
  };

  // Combine SMTP integrations with other integrations for unified display
  const allIntegrations = [
    ...integrations.map(smtp => ({
      id: `smtp-${smtp.id}`,
      originalId: smtp.id,
      type: "smtp",
      name: smtp.name,
      subType: "SMTP Email",
      host: smtp.host,
      port: smtp.port,
      username: smtp.username,
      from_email: smtp.from_email,
      from_name: smtp.from_name,
      encryption: smtp.encryption,
      success_count: smtp.success_count || 0,
      error_count: smtp.error_count || 0,
      status: smtp.status || "disconnected",
      isEnabled: smtp.is_enabled !== false,
      lastSync: smtp.last_sync,
      endpoint: `${smtp.host}:${smtp.port}`,
      syncFrequency: "email",
    })),
    ...otherIntegrations.map(other => ({
      ...other,
      id: `other-${other.id}`,
      originalId: other.id,
      type: "other",
    }))
  ];

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Integrations</h2>
          <p className="text-muted-foreground">Manage SMTP email providers and external system connections</p>
        </div>
        <div className="flex gap-3">
          {integrations.length === 0 ? (
            <Button onClick={handleCreateNew} iconName="Plus" iconPosition="left">
              Add SMTP
            </Button>
          ) : (
            <Button onClick={() => {
              if (integrations[0]) handleEdit(integrations[0]);
            }} iconName="Edit" iconPosition="left">
              Edit SMTP
            </Button>
          )}
          <Button variant="default" onClick={() => setShowOtherModal(true)} iconName="Plus" iconPosition="left">
            Add Integration
          </Button>
        </div>
      </div>

      {/* UNIFIED INTEGRATIONS CARDS GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {allIntegrations.map((item) => (
          <div key={item.id} className="border border-border rounded-xl p-6 bg-card shadow-sm hover:shadow-md transition-shadow">
            {/* Header with icon and status */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-3 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  item.type === "smtp" ? "bg-blue-100" : "bg-purple-100"
                }`}>
                  <Icon 
                    name={item.type === "smtp" ? "Mail" : "Zap"} 
                    size={20} 
                    className={item.type === "smtp" ? "text-blue-600" : "text-purple-600"} 
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-lg truncate">{item.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {item.type === "smtp" ? "SMTP Email Provider" : item.type}
                  </p>
                </div>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}
              >
                {item.status || "disconnected"}
              </span>
            </div>

            {/* Content based on type */}
            <div className="space-y-2 text-sm">
              {item.type === "smtp" ? (
                <>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">Host:</span>
                    <span className="font-mono text-xs font-medium">{item.host}:{item.port}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">Username:</span>
                    <span className="font-mono text-xs">{item.username}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">From Email:</span>
                    <span className="text-xs">{item.from_email}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">Encryption:</span>
                    <span className="text-xs font-medium uppercase">{item.encryption || 'None'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">Success Count:</span>
                    <span className="text-xs font-medium text-green-600">{item.success_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">Error Count:</span>
                    <span className="text-xs font-medium text-red-600">{item.error_count || 0}</span>
                  </div>
                  {item.lastSync && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-muted-foreground">Last Test:</span>
                      <span className="text-xs">{formatLastSync(item.lastSync)}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">Endpoint:</span>
                    <span className="font-mono text-xs truncate max-w-[200px]" title={item.endpoint}>
                      {item.endpoint}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">Sync Frequency:</span>
                    <span className="text-xs font-medium capitalize">{item.syncFrequency}</span>
                  </div>
                  <div className="flex justify-between items-center py-1">
                    <span className="text-muted-foreground">Last Sync:</span>
                    <span className="text-xs">{item.lastSync ? formatLastSync(item.lastSync) : 'Never'}</span>
                  </div>
                  {item.errorCount > 0 && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-muted-foreground">Errors:</span>
                      <span className="text-xs font-medium text-red-600">{item.errorCount} recent</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 mt-6 pt-4 border-t border-border">
              {item.type === "smtp" ? (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    iconName="Zap"
                    onClick={() => handleTest(item.originalId)}
                    disabled={testingSmtpId === item.originalId}
                    loading={testingSmtpId === item.originalId}
                  >
                    Test
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    iconName="Edit"
                    onClick={() => {
                      const originalSmtp = integrations.find(i => i.id === item.originalId);
                      if (originalSmtp) handleEdit(originalSmtp);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    iconName="Trash2"
                    onClick={() => handleDelete(item.originalId)}
                  >
                    Delete
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    iconName="Zap"
                    onClick={() => {
                      const originalOther = otherIntegrations.find(i => i.id === item.originalId);
                      if (originalOther) handleTestOtherConnection(originalOther);
                    }}
                    disabled={testingConnection === item.originalId || !item.isEnabled}
                    loading={testingConnection === item.originalId}
                  >
                    Test
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    iconName="Edit"
                    onClick={() => {
                      const originalOther = otherIntegrations.find(i => i.id === item.originalId);
                      if (originalOther) handleEditOtherIntegration(originalOther);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    iconName="Trash2"
                    onClick={() => handleDeleteOtherIntegration(item.originalId)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </div>

            {/* Enable/Disable toggle for other integrations only */}
            {item.type !== "smtp" && (
              <div className="mt-4 pt-3 border-t border-border">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    checked={item.isEnabled}
                    onChange={() => handleToggleOtherIntegration(item.originalId)}
                    label="Enabled"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* SMTP CREATE/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card p-6 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-6">
              {isCreatingNew ? "Add SMTP Configuration" : "Edit SMTP Configuration"}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {isCreatingNew 
                ? "Set up your SMTP email provider settings." 
                : "Update your SMTP email provider settings. Leave password blank to keep the current password."}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="SMTP Name *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Zoho SMTP"
              />
              <Input
                label="Host *"
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                placeholder="smtp.example.com"
              />
              <Input
                label="Port *"
                type="number"
                value={form.port}
                onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 0 })}
                placeholder="587"
              />
              <Input
                label="Username *"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="user@example.com"
              />
              <Input
                label="Password *"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={isCreatingNew ? "Enter password" : "Leave blank to keep current"}
                required={isCreatingNew}
              />
              <Select
                label="Encryption"
                value={form.encryption}
                onChange={(value) => setForm({ ...form, encryption: value })}
                options={[
                  { label: "TLS", value: "tls" },
                  { label: "SSL", value: "ssl" },
                  { label: "None", value: "" },
                ]}
              />
              <Input
                label="From Email *"
                value={form.from_email}
                onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                placeholder="sender@example.com"
              />
              <Input
                label="From Name"
                value={form.from_name}
                onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                placeholder="Your Company Name"
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={closeModal}>Cancel</Button>
              <Button onClick={isCreatingNew ? handleCreate : handleUpdate}>
                {isCreatingNew ? "Create SMTP" : "Update SMTP"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* OTHER INTEGRATIONS MODAL */}
      {showOtherModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-card-foreground">
                {editingOtherIntegration ? 'Edit Integration' : 'Add New Integration'}
              </h3>
              <Button variant="ghost" size="icon" onClick={closeOtherModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Integration Name"
                  type="text"
                  value={newOtherIntegration?.name}
                  onChange={(e) => setNewOtherIntegration(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="e.g., SAP ERP System"
                  required
                />
                
                <Select
                  label="Integration Type"
                  options={integrationTypes}
                  value={newOtherIntegration?.type}
                  onChange={(value) => setNewOtherIntegration(prev => ({ ...prev, type: value }))}
                  required
                />
              </div>

              <Input
                label="API Endpoint"
                type="url"
                value={newOtherIntegration?.endpoint}
                onChange={(e) => setNewOtherIntegration(prev => ({ ...prev, endpoint: e?.target?.value }))}
                placeholder="https://api.example.com/v1"
                description="Full URL to the API endpoint"
                required
              />

              <Input
                label="API Key / Token"
                type="password"
                value={newOtherIntegration?.apiKey}
                onChange={(e) => setNewOtherIntegration(prev => ({ ...prev, apiKey: e?.target?.value }))}
                placeholder="Enter API key or authentication token"
                description="This will be encrypted and stored securely"
              />

              <Select
                label="Sync Frequency"
                options={syncFrequencyOptions}
                value={newOtherIntegration?.syncFrequency}
                onChange={(value) => setNewOtherIntegration(prev => ({ ...prev, syncFrequency: value }))}
                description="How often should data be synchronized"
              />

              <Checkbox
                label="Enable Integration"
                description="Start synchronization immediately after saving"
                checked={newOtherIntegration?.isEnabled}
                onChange={(e) => setNewOtherIntegration(prev => ({ ...prev, isEnabled: e?.target?.checked }))}
              />
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button variant="outline" onClick={closeOtherModal}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={editingOtherIntegration ? handleUpdateOtherIntegration : handleAddOtherIntegration}
                disabled={!newOtherIntegration?.name || !newOtherIntegration?.type || !newOtherIntegration?.endpoint}
              >
                {editingOtherIntegration ? 'Update Integration' : 'Create Integration'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationTab;